import { useEffect, useState, type ReactNode } from 'react';
import { Empty, Select } from 'antd';
import type { SelectProps as AntSelectProps } from 'antd/es/select';
import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, Icon, isBottomReached, SpinLoader } from '@/modules/ui';

import * as felaRules from './SelectInput.rules';

const { Option, OptGroup } = Select;

interface IOption {
    label: ReactNode;
    value: string;
    title?: string;
    key?: string;
}

interface IOptionGroup {
    key: string;
    label: ReactNode;
    options: IOption[];
}

// eslint-disable-next-line no-prototype-builtins
const isOptionGroup = (item: IOptionGroup | IOption): item is IOptionGroup => item.hasOwnProperty('options');

function Spinner() {
    const intl = useIntl();

    return (
        <SpinLoader
            width={24}
            height={24}
            alt={intl.formatMessage({
                id: 'select.loading',
            })}
        />
    );
}

function renderOptions({ options, className }: { options: (IOption | IOptionGroup)[]; className: string }) {
    return options.map(item => {
        if (isOptionGroup(item)) {
            return (
                <OptGroup key={item.key} label={item.label}>
                    {renderOptions({
                        options: item.options,
                        className,
                    })}
                </OptGroup>
            );
        }
        return (
            <Option
                key={item.key ?? item.value}
                value={item.value}
                title={item.title}
                className={className}
                data-testid={`${item.value}-select-option`}
            >
                {item.label}
            </Option>
        );
    });
}

export interface SelectInputProps extends Omit<AntSelectProps, 'options' | 'notFoundContent'> {
    options: (IOption | IOptionGroup)[];
    empty?: ReactNode;
    customStyle?: typeof felaRules.select;
    customOptionStyle?: typeof felaRules.option;
    hasMore?: boolean;
    loadMore?: () => void;
    loadingMore?: boolean;
    popupShouldIgnoreParentContainer?: boolean;
}

export const SelectInput = ({
    id,
    open = false,
    onOpenChange,
    options,
    empty,
    customStyle,
    customOptionStyle,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    popupShouldIgnoreParentContainer = false,
    ...rest
}: SelectInputProps) => {
    const { css } = useFela();

    const [innerOpen, setOpen] = useState(open);

    useEffect(() => {
        setOpen(open);
    }, [open]);

    return (
        <Select
            id={id}
            data-testid={`${id}-select-input`}
            className={css(felaRules.select, customStyle)}
            getPopupContainer={triggerNode => (popupShouldIgnoreParentContainer ? undefined : triggerNode.parentElement)}
            suffixIcon={<Icon color='black' type='dropdown' customStyle={innerOpen ? felaRules.arrowOpen : felaRules.arrow} />}
            {...rest}
            open={innerOpen}
            classNames={{ popup: { root: css(felaRules.dropdown) } }}
            onOpenChange={o => {
                setOpen(o);
                onOpenChange?.(o);
            }}
            notFoundContent={loading ? null : <Empty image={null} imageStyle={{ display: 'none' }} description={empty} />}
            loading={loading}
            onPopupScroll={event => {
                const bottomReached = isBottomReached(event.target as HTMLDivElement);

                if (!loadingMore && bottomReached) {
                    loadMore?.();
                }
            }}
        >
            {loading ? (
                <Option disabled className={css(felaRules.infinite)}>
                    <Spinner />
                </Option>
            ) : (
                <>
                    {renderOptions({
                        options,
                        className: css(felaRules.option, customOptionStyle),
                    })}
                    {loadingMore ? (
                        <Option disabled className={css(felaRules.infinite)}>
                            <Spinner />
                        </Option>
                    ) : null}
                    {hasMore && !loadingMore ? (
                        <Option disabled className={css(felaRules.infinite)}>
                            <Button type='link' onClick={loadMore}>
                                <FormattedMessage id='select.loadMore' />
                            </Button>
                        </Option>
                    ) : null}
                </>
            )}
        </Select>
    );
};
